import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import LineChart from "../../components/LineChart";
import { FILTER_TYPES, GRAPH_TYPES } from "../../utils/enums";
import {
  usePostApplicationExportCSVMutation,
  usePostApplicationGraphMutation,
  usePostRevenueExportCSVMutation,
  usePostRevenueGraphMutation,
  usePostUserExportCSVMutation,
  usePostUserGraphMutation,
} from "../../service/analytics";
import { toast } from "react-toastify";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#204e33" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const Reports = () => {
  const [getUserGraphData] = usePostUserGraphMutation();
  const [getUserExportCSV] = usePostUserExportCSVMutation();
  const [getApplicationGraphData] = usePostApplicationGraphMutation();
  const [getApplicationExportCSV] = usePostApplicationExportCSVMutation();
  const [getRevenueGraphData] = usePostRevenueGraphMutation();
  const [getRevenueExportCSV] = usePostRevenueExportCSVMutation();

  const [userNames, setUserNames] = useState<string[]>([]);
  const [userValues, setUserValues] = useState<number[]>([]);
  const [salariedNames, setSalariedNames] = useState<string[]>([]);
  const [salariedValues, setSalariedValues] = useState<number[]>([]);
  // const [freelancerNames, setFreelancerNames] = useState<string[]>([]);
  // const [freelancerValues, setFreelancerValues] = useState<number[]>([]);
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [companyValues, setCompanyValues] = useState<number[]>([]);
  const [applicationNames, setApplicationNames] = useState<string[]>([]);
  const [applicationValues, setApplicationValues] = useState<number[]>([]);
  const [revenueNames, setRevenueNames] = useState<string[]>([]);
  const [revenueValues, setRevenueValues] = useState<number[]>([]);
  const [individualFilter, setIndividualFilter] = useState(
    FILTER_TYPES.MONTHLY
  );
  const [salariedFilter, setSalariedFilter] = useState(FILTER_TYPES.MONTHLY);
  // const [freelancerFilter, setFreelancerFilter] = useState(
  //   FILTER_TYPES.MONTHLY
  // );
  const [companyFilter, setCompanyFilter] = useState(FILTER_TYPES.MONTHLY);
  const [revenueFilter, setRevenueFilter] = useState(FILTER_TYPES.MONTHLY);
  const [applicationsFilter, setApplicationsFilter] = useState(
    FILTER_TYPES.MONTHLY
  );

  const handleIndividuals = (event: SelectChangeEvent) => {
    setIndividualFilter(event.target.value as FILTER_TYPES);
  };
  const handleSalariedFilter = (event: SelectChangeEvent) => {
    setSalariedFilter(event.target.value as FILTER_TYPES);
  };
  // const handleFreelancerFilter = (event: SelectChangeEvent) => {
  //   setFreelancerFilter(event.target.value as FILTER_TYPES);
  // };

  const handleCompaniesFilter = (event: SelectChangeEvent) => {
    setCompanyFilter(event.target.value as FILTER_TYPES);
  };
  const handleRevenueFilter = (event: SelectChangeEvent) => {
    setRevenueFilter(event.target.value as FILTER_TYPES);
  };
  const handleApplicationsFilter = (event: SelectChangeEvent) => {
    setApplicationsFilter(event.target.value as FILTER_TYPES);
  };

  useEffect(() => {
    getUserGraph();
  }, [individualFilter]);

  const getUserGraph = async () => {
    const body = {
      interval: Number(individualFilter),
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getUserGraphData({
        id: GRAPH_TYPES.USER,
        body: encryptedBody,
      }).unwrap();
      if (response?.status === 200 && response?.data) {
        const entries = Object.entries(response.data); // Converts object to array of [key, value]

        setUserNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
        setUserValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getSalariedGraph();
  }, [salariedFilter]);

  const getSalariedGraph = async () => {
    const body = {
      interval: Number(salariedFilter),
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getUserGraphData({
        id: GRAPH_TYPES.SALARIED,
        body: encryptedBody,
      }).unwrap();

      if (response?.status === 200 && response?.data) {
        const entries = Object.entries(response.data); // Converts object to array of [key, value]
        setSalariedNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
        setSalariedValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  // useEffect(() => {
  //   getFreelancerGraph();
  // }, [freelancerFilter]);

  // const getFreelancerGraph = async () => {
  //   const body = {
  //     interval: Number(freelancerFilter),
  //   };
  //   try {
  //     const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
  //     const response = await getUserGraphData({
  //       id: GRAPH_TYPES.FREELANCER,
  //       body: encryptedBody,
  //     }).unwrap();

  //     if (response?.status === 200 && response?.data) {
  //       const entries = Object.entries(response.data); // Converts object to array of [key, value]
  //       setFreelancerNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
  //       setFreelancerValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
  //     }
  //   } catch (error: any) {
  //     toast.error(error?.data?.message || "");
  //   }
  // };

  useEffect(() => {
    getCompaniesGraph();
  }, [companyFilter]);

  const getCompaniesGraph = async () => {
    const body = {
      interval: Number(companyFilter),
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getUserGraphData({
        id: GRAPH_TYPES.COMPANY,
        body: encryptedBody,
      }).unwrap();

      if (response?.status === 200 && response?.data) {
        const entries = Object.entries(response.data); // Converts object to array of [key, value]
        setCompanyNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
        setCompanyValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getApplicationGraph();
  }, [applicationsFilter]);

  const getApplicationGraph = async () => {
    const body = {
      interval: Number(applicationsFilter),
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getApplicationGraphData({
        body: encryptedBody,
      }).unwrap();

      if (response?.status === 200 && response?.data) {
        const entries = Object.entries(response.data); // Converts object to array of [key, value]
        setApplicationNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
        setApplicationValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getRevenueGraph();
  }, [revenueFilter]);

  const getRevenueGraph = async () => {
    const body = {
      interval: Number(revenueFilter),
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getRevenueGraphData({
        body: encryptedBody,
      }).unwrap();
      if (response?.status === 200 && response?.data) {
        const entries = Object.entries(response.data); // Converts object to array of [key, value]
        setRevenueNames(entries.map(([key]) => key)); // e.g., ["Jan", "Feb", ...]
        setRevenueValues(entries.map(([, value]) => value)); // e.g., [0, 0, 87, 70, ...]
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  const getExportCSV = async (type: number, int: number) => {
    const body = {
      interval: int,
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getUserExportCSV({
        id: type,
        body: encryptedBody,
      }).unwrap();
      if (response?.status === 200) {
        window.open(response?.file_url);
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  const getExportAppCSV = async (int: number) => {
    const body = {
      interval: int,
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getApplicationExportCSV({
        body: encryptedBody,
      }).unwrap();
      if (response?.status === 200) {
        window.open(response?.file_url);
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  const getExportRevenueCSV = async (int: number) => {
    const body = {
      interval: int,
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getRevenueExportCSV({
        body: encryptedBody,
      }).unwrap();
      if (response?.status === 200) {
        window.open(response?.file_url);
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "");
    }
  };

  const graphIndividualData = {
    labels: userNames,
    datasets: [
      {
        label: "Individuals",
        data: userValues,
        borderColor: "#1a59cb",
        backgroundColor: "#1a59cb",
        color: "#FFFFF",
      },
    ],
  };
  const graphSalariedAgentData = {
    labels: salariedNames,
    datasets: [
      {
        label: "Salaried Agents",
        data: salariedValues,
        borderColor: "#1a59cb",
        backgroundColor: "#1a59cb",
        color: "#FFFFF",
      },
    ],
  };
  // const graphFreelancerData = {
  //   labels: freelancerNames,
  //   datasets: [
  //     {
  //       label: "Freelancer Agents",
  //       data: freelancerValues,
  //       borderColor: "#1a59cb",
  //       backgroundColor: "#1a59cb",
  //       color: "#FFFFF",
  //     },
  //   ],
  // };
  const graphCompanyData = {
    labels: companyNames,
    datasets: [
      {
        label: "Companies",
        data: companyValues,
        borderColor: "#1a59cb",
        backgroundColor: "#1a59cb",
        color: "#FFFFF",
      },
    ],
  };
  const graphApplicationsData = {
    labels: applicationNames,
    datasets: [
      {
        label: "Applications",
        data: applicationValues,
        borderColor: "#1a59cb",
        backgroundColor: "#1a59cb",
        color: "#FFFFF",
      },
    ],
  };
  const graphRevenueData = {
    labels: revenueNames,
    datasets: [
      {
        label: "Revenue",
        data: revenueValues,
        borderColor: "#1a59cb",
        backgroundColor: "#1a59cb",
        color: "#FFFFF",
      },
    ],
  };
  return (
    <MainContainer>
      <div className="main_title fdx">
        <h1>Reports and Analytics</h1>
        {/* <Box>
          <Button className="btn btn_blue">Export CSV</Button>
        </Box> */}
      </div>

      <div className="grid_sc gap_m ">
        <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total Individuals</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() =>
                  getExportCSV(GRAPH_TYPES?.USER, Number(individualFilter))
                }
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
                {/* Export CSV */}
              </Button>

              <Select
                className="dashSelect"
                labelId="individualFilter"
                id="individualFilter"
                value={individualFilter}
                onChange={handleIndividuals}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>
          <LineChart data={graphIndividualData} />
        </Item>

        {/* <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total Freelancer Agents</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() =>
                  getExportCSV(GRAPH_TYPES?.FREELANCER, freelancerFilter)
                }
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Button>
              <Select
                className="dashSelect"
                labelId="freelancerFilter"
                id="freelancerFilter"
                value={freelancerFilter}
                onChange={handleFreelancerFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>

          <LineChart data={graphFreelancerData} />
        </Item> */}
        <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total B2Bs</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() =>
                  getExportCSV(GRAPH_TYPES?.COMPANY, Number(companyFilter))
                }
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Button>
              <Select
                className="dashSelect"
                labelId="companyFilter"
                id="companyFilter"
                value={companyFilter}
                onChange={handleCompaniesFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>

          <LineChart data={graphCompanyData} />
        </Item>
        <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total Wizard Team</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() =>
                  getExportCSV(GRAPH_TYPES?.SALARIED, Number(salariedFilter))
                }
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Button>
              <Select
                className="dashSelect"
                labelId="salariedFilter"
                id="salariedFilter"
                value={salariedFilter}
                onChange={handleSalariedFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>
          <LineChart data={graphSalariedAgentData} />
        </Item>
        <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total Applications</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() => getExportAppCSV(Number(applicationsFilter))}
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Button>
              <Select
                className="dashSelect"
                labelId="applicationsFilter"
                id="applicationsFilter"
                value={applicationsFilter}
                onChange={handleApplicationsFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>

          <LineChart data={graphApplicationsData} />
        </Item>
        <Item className="cards" style={{ borderRadius: "15px" }}>
          <div className="analytics">
            <h2 className="mn_hdng">Total Revenue</h2>
            <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <Button
                onClick={() => getExportRevenueCSV(Number(revenueFilter))}
                className="btn btn_blue"
                style={{ height: "38px" }}
              >
                <img
                  src="/static/images/downloadArrow.png"
                  alt="download"
                  style={{
                    height: "20px",
                    width: "20px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Button>
              <Select
                className="dashSelect"
                labelId="revenueFilter"
                id="revenueFilter"
                value={revenueFilter}
                onChange={handleRevenueFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.DAILY}>Daily</MenuItem>
                <MenuItem value={FILTER_TYPES.WEEKLY}>Weekly</MenuItem>
                <MenuItem value={FILTER_TYPES.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={FILTER_TYPES.YEARLY}>Yearly</MenuItem>
              </Select>
            </Box>
          </div>

          <LineChart data={graphRevenueData} />
        </Item>
      </div>
    </MainContainer>
  );
};

export default Reports;
